import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

/** 등급 확률 1행 — 라벨(등급명, 등급색) + 백분율 문구. boot가 rarityLabel/RARITY_COLOR/% 포맷을 합성해 주입. */
export interface GoldBoxOddsRow {
  readonly label: string;
  /** 등급 색 hex(#rrggbb) — 등급명 텍스트 색. */
  readonly color: string;
  readonly percentText: string;
}

export interface GoldBoxOddsPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly title: string;
  readonly rows: readonly GoldBoxOddsRow[];
  /** 천장 안내 문구 ("N회 구매마다 X 이상 확정"). */
  readonly pityText: string;
  /** 천장까지 현재 남은 구매 횟수 안내 ("(현재 N회 남음)"). */
  readonly pityRemainingText: string;
  readonly closeText: string;
  readonly onClose: () => void;
}

const PANEL_W = 280;
const ROW_H = 28;
const HEAD_H = 56; // 제목 영역
const PITY_H = 58; // 천장 안내(규칙 + 잔여 2줄)
const FOOT_H = 56; // 닫기 버튼

/**
 * 골드 구슬 파이프 상자 확률(?) 팝업 — RebirthConfirmPopup 패턴(ModalLayer + NineSlicePanel + 닫기).
 * 등급별 획득 확률 목록 + 천장 안내를 표시한다(읽기 전용). 표시 데이터(라벨/색/% /천장 문구)는 boot가
 * computeRarityOdds + rarityLabel + RARITY_COLOR로 합성해 주입 — 본 팝업은 렌더만 책임(Humble View).
 */
export class GoldBoxOddsPopup extends ModalLayer {
  constructor(scene: Phaser.Scene, opts: GoldBoxOddsPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onClose(),
    });

    const panelH = HEAD_H + opts.rows.length * ROW_H + PITY_H + FOOT_H;
    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const top = cy - panelH / 2;
    const dir = getTextDirection();
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, panelH);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: panelH,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const title = scene.add
      .text(cx, top + 22, opts.title, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtnX = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, dir), top + 18, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtnX);
    closeBtnX.on("pointerup", () => opts.onClose());
    this.contentContainer.add([title, closeBtnX]);

    // 등급 행 — 좌측 등급명(등급색) + 우측 확률%. RTL이면 좌우 반전.
    const padX = 28;
    const leftX = dir === "rtl" ? cx + PANEL_W / 2 - padX : cx - PANEL_W / 2 + padX;
    const rightX = dir === "rtl" ? cx - PANEL_W / 2 + padX : cx + PANEL_W / 2 - padX;
    const leftOrigin = dir === "rtl" ? 1 : 0;
    const rightOrigin = dir === "rtl" ? 0 : 1;
    opts.rows.forEach((row, i) => {
      const ry = top + HEAD_H + i * ROW_H + ROW_H / 2;
      const name = scene.add
        .text(leftX, ry, row.label, {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.body,
          color: row.color,
        })
        .setOrigin(leftOrigin, 0.5);
      const pct = scene.add
        .text(rightX, ry, row.percentText, {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.body,
          color: PALETTE.textPrimary,
        })
        .setOrigin(rightOrigin, 0.5);
      this.contentContainer.add([name, pct]);
    });

    // 천장 안내 — 등급 목록 아래. 규칙(상) + 현재 남은 횟수(하) 2줄.
    const pityTop = top + HEAD_H + opts.rows.length * ROW_H;
    const pity = scene.add
      .text(cx, pityTop + 14, opts.pityText, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textBright,
        align: "center",
      })
      .setOrigin(0.5)
      .setWordWrapWidth(PANEL_W - 36);
    const pityRemaining = scene.add
      .text(cx, pityTop + 38, opts.pityRemainingText, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.positive,
        align: "center",
      })
      .setOrigin(0.5);
    this.contentContainer.add([pity, pityRemaining]);

    const closeBtn = new NineSliceButton(scene, cx, top + panelH - 26, {
      style: PRIMARY_BUTTON_STYLE,
      label: opts.closeText,
      widthOverride: 140,
    });
    closeBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onClose());
    this.contentContainer.add(closeBtn);
  }
}
